import { useEffect, useMemo, useState } from 'react';
import {
    collection, onSnapshot, query, where,
} from 'firebase/firestore';
import { useOutletContext } from 'react-router-dom';
import { useCurrentUser } from '@rainbow-modules/firebase-hooks';
import { ViewContextType } from 'pages/Opportunities/types';
import useTodoCollection from 'data/firestore/agent/todo/useCollection';
import { db } from 'data/firestore/firebase';
import { EntityGet } from 'data/firestore/types';
import { Todo } from 'data/firestore/agent/todo/types';
import useBoardOpportunitiesFromFirestore from './useBoardOpportunitiesFromFirestore';
import useBoardOpportunitiesFromAlgolia from './useBoardOpportunitiesFromAlgolia';

type UserNotification = {
    type: string;
    status: 'unread' | 'read' | 'dismissed';
    entityRef?: {
        type?: string;
        id?: string;
    };
};

type UnreadNotes = {
    count: number;
    notificationIds: string[];
};

const useBoardOpportunities = (agentId: string) => {
    const { uid: userId = '' } = useCurrentUser() || {};
    const {
        selectedPipeline = '',
        stages = [],
        sortBy,
        activeFiltersCount = 0,
        searchQuery,
    } = useOutletContext<ViewContextType>();

    const searchEnabled = activeFiltersCount > 0 || Boolean(searchQuery);

    const { isLoading, data } = useBoardOpportunitiesFromFirestore(
        agentId,
        {
            disabled: !agentId || !selectedPipeline || !stages.length,
            track: [agentId, selectedPipeline, sortBy?.order, sortBy?.sortBy],
        },
    );

    const { data: searchResults, aggregatedTotals } = useBoardOpportunitiesFromAlgolia({
        agentId,
        enabled: true,
    });

    const { data: todos = [] } = useTodoCollection(agentId, {
        listQueryFn: (ref) => query(
            ref,
            where('pipelineId', '==', selectedPipeline),
            where('status', 'in', ['pending', 'in_progress', 'done']),
        ),
        disabled: !agentId || !selectedPipeline,
        track: [agentId, selectedPipeline],
    });

    const todoStatsByOpportunityId = useMemo(
        () => todos.reduce(
            (acc, todo: EntityGet<Todo>) => {
                const opportunityId = todo?.opportunityId;
                if (!opportunityId) return acc;
                const current = acc[opportunityId] || {
                    pendingCount: 0,
                    doneCount: 0,
                    totalCount: 0,
                };

                const isDone = todo.status === 'done';
                const pendingCount = current.pendingCount + (isDone ? 0 : 1);
                const doneCount = current.doneCount + (isDone ? 1 : 0);
                const totalCount = pendingCount + doneCount;

                acc[opportunityId] = {
                    pendingCount,
                    doneCount,
                    totalCount,
                };

                return acc;
            },
            {} as Record<string, { pendingCount: number; doneCount: number; totalCount: number }>,
        ),
        [todos],
    );

    const [unreadNotesByOpportunityId, setUnreadNotesByOpportunityId] = useState<
    Record<string, UnreadNotes>
    >({});

    useEffect(() => {
        if (!agentId || !userId) {
            setUnreadNotesByOpportunityId({});
            return () => {};
        }

        const notificationsRef = collection(db, `/agent/${agentId}/user/${userId}/notification`);
        const notificationsQuery = query(
            notificationsRef,
            where('type', '==', 'note-created'),
            where('entityRef.type', '==', 'opportunity'),
            where('status', '==', 'unread'),
        );

        const unsub = onSnapshot(notificationsQuery, (querySnap) => {
            const unreadNotes = querySnap.docs.reduce(
                (acc, docSnap) => {
                    const notification = docSnap.data() as UserNotification;
                    const opportunityId = notification?.entityRef?.id;
                    if (!opportunityId) return acc;

                    const current = acc[opportunityId] || { count: 0, notificationIds: [] };
                    acc[opportunityId] = {
                        count: current.count + 1,
                        notificationIds: [...current.notificationIds, docSnap.id],
                    };
                    return acc;
                },
                {} as Record<string, UnreadNotes>,
            );
            setUnreadNotesByOpportunityId(unreadNotes);
        });

        return () => {
            unsub();
        };
    }, [agentId, userId]);

    const opportunities = useMemo(
        () => {
            const selectedOpportunities = (searchEnabled ? searchResults : data) ?? {};

            return Object.entries(selectedOpportunities).reduce(
                (acc, [stageId, stageOpportunities]) => {
                    acc[stageId] = stageOpportunities.map((opportunity) => ({
                        ...opportunity,
                        todoStats: todoStatsByOpportunityId[opportunity.id] || {
                            pendingCount: 0,
                            doneCount: 0,
                            totalCount: 0,
                        },
                        unreadNotes: unreadNotesByOpportunityId[opportunity.id] || {
                            count: 0,
                            notificationIds: [],
                        },
                    }));

                    return acc;
                },
                {} as Record<string, Array<Record<string, unknown>>>,
            );
        },
        [searchEnabled, searchResults, data, todoStatsByOpportunityId, unreadNotesByOpportunityId],
    );

    return {
        isLoading,
        opportunities,
        totals: aggregatedTotals,
    };
};

export default useBoardOpportunities;
